import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  BarChart3,
  ChevronRight,
  ClipboardCheck,
  Gift,
  Receipt,
  Server,
  Workflow,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { FinancePnlDataQualityStatus, FinancePnlSnapshot } from '@nadoutong/contracts';
import { OpsApiError } from '../../api/client.ts';
import { useFinancePnl } from '../../api/finance.ts';
import { usePricing } from '../../api/pricing.ts';
import { formatCnyCents, formatUsdCentsAsCny } from '../../components/formatters.ts';
import { StatusChip } from '../../components/StatusChip.tsx';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Button, KpiCard, Panel, Skeleton } from '../../ui/index.ts';
import type { KpiTone } from '../../ui/index.ts';
import { parsePnlWindow } from './hubState.ts';
import type { MetricsTabContext } from './hubState.ts';

/**
 * 盈亏 tab (epic #1643 S5): the true-P&L snapshot absorbed from the
 * standalone `/business/revenue` page — operating KPI wall, money-flow
 * / attribution-quality panels, and the finance drilldown links, all
 * from `/v1/ops/finance/pnl`. Unlike the other tabs it serves the FULL
 * `FinancePnlWindow` set, so it reads the raw `?window=` param itself
 * (via `parsePnlWindow`) rather than the context's narrowed window; the
 * hub's pill row surfaces the extended presets while this tab is active.
 */

export function PnlTab({ context }: { readonly context: MetricsTabContext }): JSX.Element {
  const t = useT();
  const [params] = useSearchParams();
  const window = parsePnlWindow(params.get('window'));
  const { brand, customFrom, customTo } = context;
  const query = useFinancePnl({ window, brand, from: customFrom, to: customTo });
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;

  return (
    <div className="flex flex-col gap-6" data-testid="metrics-pnl-tab">
      {query.isSuccess ? (
        <div className="flex items-center justify-end gap-3">
          {query.data.materialized_through !== undefined ? (
            <p
              className="m-0 text-dense text-paper-low"
              data-testid="finance-pnl-materialized-through"
            >
              {t('finance.pnl.materialized_through', { date: query.data.materialized_through })}
            </p>
          ) : null}
          <QualityChip status={query.data.data_quality.status} />
        </div>
      ) : null}

      {query.isPending ? <LoadingSkeleton /> : null}

      {query.isError ? (
        <SectionError
          label={t('finance.pnl.error')}
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : null}

      {query.isSuccess ? <PnlContent data={query.data} cnyPerUsd={cnyPerUsd} /> : null}
    </div>
  );
}

function PnlContent({
  data,
  cnyPerUsd,
}: {
  readonly data: FinancePnlSnapshot;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const { operating, cashflow, position, attribution, data_quality: quality } = data;

  return (
    <>
      <section className="grid gap-4 xl:grid-cols-4">
        <MetricCard
          testId="finance-pnl-operating-profit"
          label={t('finance.pnl.metric.operating_profit')}
          value={formatCnyCents(operating.realized_operating_profit_cny_cents, true)}
          sub={t('finance.pnl.metric.margin', { pct: formatPct(operating.margin_pct) })}
          tone={operating.realized_operating_profit_cny_cents < 0 ? 'bad' : 'good'}
        />
        <MetricCard
          testId="finance-pnl-cash-gross-profit"
          label={t('finance.pnl.metric.cash_gross_profit')}
          value={formatCnyCents(operating.cash_business_gross_profit_cny_cents, true)}
          sub={t('finance.pnl.metric.cash_revenue', {
            amount: formatCnyCents(operating.cash_funded_revenue_cny_cents, false),
          })}
          tone={operating.cash_business_gross_profit_cny_cents < 0 ? 'bad' : 'good'}
        />
        <MetricCard
          testId="finance-pnl-gifted-cost"
          label={t('finance.pnl.metric.gifted_cost')}
          value={formatCnyCents(operating.gifted_upstream_cost_cny_cents, false)}
          sub={t('finance.pnl.metric.gifted_usage', {
            amount: formatUsdBackedCny(attribution.gift_funded_usage_usd_cents, cnyPerUsd),
          })}
          tone={operating.gifted_upstream_cost_cny_cents > 0 ? 'warn' : 'neutral'}
        />
        <MetricCard
          testId="finance-pnl-net-cashflow"
          label={t('finance.pnl.metric.net_cashflow')}
          value={formatCnyCents(cashflow.net_cashflow_cny_cents, true)}
          sub={t('finance.pnl.metric.cash_in', {
            amount: formatCnyCents(cashflow.user_cash_in_cny_cents, false),
          })}
          tone={cashflow.net_cashflow_cny_cents < 0 ? 'bad' : 'good'}
        />
      </section>
      <p className="m-0 text-dense text-paper-low">{t('finance.pnl.currency_note')}</p>

      <section className="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
        <Panel
          caption={
            <span className="inline-flex items-center gap-2">
              <Workflow aria-hidden="true" className="h-4 w-4 text-signal" />
              {t('finance.pnl.flow.title')}
            </span>
          }
          padded
          testId="finance-pnl-flow"
        >
          <div className="grid gap-5 lg:grid-cols-3">
            <ValueStack
              title={t('finance.pnl.flow.user_cash')}
              rows={[
                [t('finance.pnl.flow.cash_in'), formatCnyCents(cashflow.user_cash_in_cny_cents, false)],
                [t('finance.pnl.flow.payment_fee'), formatCnyCents(cashflow.payment_fee_cny_cents, false)],
                [t('finance.pnl.flow.cash_liability'), formatUsdBackedCny(position.user_cash_liability_usd_cents, cnyPerUsd)],
              ]}
            />
            <ValueStack
              title={t('finance.pnl.flow.usage')}
              rows={[
                [t('finance.pnl.flow.cash_revenue'), formatCnyCents(operating.cash_funded_revenue_cny_cents, false)],
                [t('finance.pnl.flow.cash_cost'), formatCnyCents(operating.cash_funded_upstream_cost_cny_cents, false)],
                [t('finance.pnl.flow.gift_cost'), formatCnyCents(operating.gifted_upstream_cost_cny_cents, false)],
              ]}
            />
            <ValueStack
              title={t('finance.pnl.flow.upstream')}
              rows={[
                [t('finance.pnl.flow.upstream_cash_out'), formatCnyCents(cashflow.upstream_cash_out_cny_cents, false)],
                [t('finance.pnl.flow.upstream_prepaid'), formatUsdBackedCny(position.upstream_prepaid_remaining_usd_cents, cnyPerUsd)],
                [t('finance.pnl.flow.gift_liability'), formatUsdBackedCny(position.user_gift_liability_usd_cents, cnyPerUsd)],
              ]}
            />
          </div>
        </Panel>

        <Panel
          caption={
            <span className="inline-flex items-center gap-2">
              <ClipboardCheck aria-hidden="true" className="h-4 w-4 text-signal" />
              {t('finance.pnl.quality.title')}
            </span>
          }
          padded
          testId="finance-pnl-attribution"
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <QualityTile label={t('finance.pnl.quality.balance_coverage')} value={formatPlainPct(quality.balance_source_coverage_pct)} />
            <QualityTile label={t('finance.pnl.quality.cost_coverage')} value={formatPlainPct(quality.upstream_cost_coverage_pct)} />
            <QualityTile testId="finance-pnl-unknown-source" label={t('finance.pnl.quality.unknown_source')} value={formatUsdBackedCny(quality.unknown_source_usage_usd_cents, cnyPerUsd)} />
            <QualityTile label={t('finance.pnl.quality.missing_cost')} value={formatUsdBackedCny(quality.missing_upstream_cost_usage_usd_cents, cnyPerUsd)} />
            <QualityTile label={t('finance.pnl.quality.unallocated_debits')} value={formatUsdBackedCny(quality.unallocated_balance_debits_usd_cents, cnyPerUsd)} />
            <QualityTile label={t('finance.pnl.quality.mismatch_accounts')} value={quality.ledger_mismatch_account_count.toLocaleString('en-US')} />
          </div>
        </Panel>
      </section>

      <section className="grid gap-4 xl:grid-cols-4">
        <DrilldownLink icon={Receipt} label={t('finance.pnl.drill.payments')} to="/finance/payment-orders" />
        <DrilldownLink icon={Gift} label={t('finance.pnl.drill.gift_cards')} to="/finance/gift-cards" />
        <DrilldownLink icon={Server} label={t('finance.pnl.drill.upstreams')} to="/supply/upstreams" />
        <DrilldownLink icon={BarChart3} label={t('finance.pnl.drill.reports')} to="/finance/reports" />
      </section>
    </>
  );
}

function MetricCard({
  label,
  value,
  sub,
  tone,
  testId,
}: {
  readonly label: string;
  readonly value: string;
  readonly sub: string;
  readonly tone: 'good' | 'bad' | 'warn' | 'neutral';
  readonly testId: string;
}): JSX.Element {
  const kpiTone: KpiTone =
    tone === 'bad' ? 'bad' : tone === 'warn' ? 'warn' : tone === 'good' ? 'ok' : 'default';
  return (
    <KpiCard
      testId={testId}
      label={label}
      value={value}
      tone={kpiTone}
      lamp={tone === 'bad' ? 'bad' : tone === 'warn' ? 'warn' : undefined}
      delta={{ text: sub, tone: 'neutral' }}
    />
  );
}

function ValueStack({
  title,
  rows,
}: {
  readonly title: string;
  readonly rows: readonly (readonly [string, string])[];
}): JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      <h4 className="m-0 text-label font-medium text-paper-mid">{title}</h4>
      <dl className="m-0 grid gap-2">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-baseline justify-between gap-4">
            <dt className="text-dense text-paper-low">{label}</dt>
            <dd className="m-0 font-mono text-dense font-medium text-paper">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function QualityTile({
  label,
  value,
  testId,
}: {
  readonly label: string;
  readonly value: string;
  readonly testId?: string;
}): JSX.Element {
  return (
    <div className="flex min-h-[72px] flex-col justify-center gap-1 rounded-lg bg-well p-4" data-testid={testId}>
      <p className="m-0 text-label text-paper-low">{label}</p>
      <p className="m-0 font-mono text-title text-paper">{value}</p>
    </div>
  );
}

function QualityChip({ status }: { readonly status: FinancePnlDataQualityStatus }): JSX.Element {
  const t = useT();
  const variant = status === 'balanced' ? 'ok' : status === 'partial' ? 'warning' : 'error';
  const key: I18nKey =
    status === 'balanced'
      ? 'finance.pnl.quality.status.balanced'
      : status === 'partial'
        ? 'finance.pnl.quality.status.partial'
        : 'finance.pnl.quality.status.unreliable';
  return <StatusChip variant={variant} label={t(key)} testId="finance-pnl-quality-status" />;
}

function DrilldownLink({
  icon: Icon,
  label,
  to,
}: {
  readonly icon: LucideIcon;
  readonly label: string;
  readonly to: string;
}): JSX.Element {
  return (
    <Link
      to={to}
      className="flex min-h-[72px] items-center justify-between rounded-lg border border-line-soft bg-card p-5 text-body font-medium text-paper transition-colors duration-120 hover:border-line-strong hover:bg-raised"
    >
      <span className="flex items-center gap-3">
        <Icon aria-hidden="true" className="h-4 w-4 text-signal" />
        {label}
      </span>
      <ChevronRight aria-hidden="true" className="h-4 w-4 text-paper-low" />
    </Link>
  );
}

function LoadingSkeleton(): JSX.Element {
  return (
    <div className="grid gap-4 xl:grid-cols-4" data-testid="finance-pnl-loading" aria-hidden="true">
      {['a', 'b', 'c', 'd'].map((id) => (
        <Skeleton key={id} className="h-[156px]" />
      ))}
    </div>
  );
}

function SectionError({
  label,
  error,
  onRetry,
}: {
  readonly label: string;
  readonly error: Error;
  readonly onRetry: () => void;
}): JSX.Element {
  const t = useT();
  const message = error instanceof OpsApiError ? `${error.code}: ${error.message}` : error.message;
  return (
    <div
      className="flex items-center justify-between gap-4 rounded-lg border border-bad-line bg-bad-dim p-4"
      data-testid="finance-pnl-error"
    >
      <div className="flex items-center gap-3">
        <AlertCircle aria-hidden="true" className="h-4 w-4 shrink-0 text-bad" />
        <p className="m-0 text-body font-medium text-bad-bright">{label}</p>
        <p className="m-0 text-dense text-bad-bright">{message}</p>
      </div>
      <Button variant="secondary" size="sm" onClick={onRetry}>
        {t('finance.pnl.retry')}
      </Button>
    </div>
  );
}

function formatPct(value: number): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${(value * 100).toFixed(2)}%`;
}

function formatPlainPct(value: number): string {
  if (!Number.isFinite(value)) return '0.00%';
  return `${(value * 100).toFixed(2)}%`;
}

function formatUsdBackedCny(usdCents: number, cnyPerUsd: number | null): string {
  return formatUsdCentsAsCny(usdCents, cnyPerUsd, false);
}
